import React, { useEffect, useMemo, useRef, useState } from 'react';

import { ScheduleEntry } from 'us-web-services';
import {
  HOUR_HEIGHT,
  WEEKDAYS,
  addDays,
  entryCoversDay,
  isSameDay,
  startOfWeek,
  toDateKey,
} from '../utils';
import { TimeGridColumn } from './TimeGridColumn';

interface Props {
  cursor: Date;
  schedule: ScheduleEntry[];
  canEdit: boolean;
  onCreate: (start: Date, end: Date) => void;
  onEditAvailability: (entry: ScheduleEntry) => void;
  onOpenBooking: (entry: ScheduleEntry) => void;
  namespace?: string;
  showBookingDetails?: boolean;
}

export const WeekView = ({
  cursor,
  schedule,
  canEdit,
  onCreate,
  onEditAvailability,
  onOpenBooking,
  namespace,
  showBookingDetails,
}: Props): JSX.Element => {
  const scrollRef = useRef<HTMLDivElement | null>(null);
  const [now, setNow] = useState(() => new Date());

  useEffect(() => {
    if (scrollRef.current) scrollRef.current.scrollTop = HOUR_HEIGHT * 7;
  }, []);

  useEffect(() => {
    const t = setInterval(() => setNow(new Date()), 60 * 1000);
    return (): void => clearInterval(t);
  }, []);

  const weekStart = startOfWeek(cursor);
  const days = Array.from({ length: 7 }, (_, i) => addDays(weekStart, i));

  const byDay = useMemo(() => {
    const map = new Map<string, ScheduleEntry[]>();
    for (const day of days) {
      const arr: ScheduleEntry[] = [];
      for (const entry of schedule) {
        if (entryCoversDay(entry.start, entry.end, day)) arr.push(entry);
      }
      map.set(toDateKey(day), arr);
    }
    return map;

  }, [schedule, weekStart.getTime()]);

  const today = new Date();

  return (
    <div className='bg-white border-2 border-sapphire-50 rounded-[20px] break-words p-[15px] mb-[15px] flex flex-col overflow-hidden'>
      <div
        className='grid border-b border-neutral-200 bg-white'
        style={{ gridTemplateColumns: '48px repeat(7, 1fr)' }}
      >
        <div />
        {days.map((d) => {
          const isToday = isSameDay(d, today);
          return (
            <div
              key={toDateKey(d)}
              className={`py-2 text-center text-sm border-l border-neutral-200 ${
                isToday ? 'text-sapphire font-bold' : 'text-neutral-700'
              }`}
            >
              <div className='text-[10px] uppercase tracking-wide'>
                {WEEKDAYS[d.getDay()]}
              </div>
              <div className='text-lg leading-none'>{d.getDate()}</div>
            </div>
          );
        })}
      </div>

      <div ref={scrollRef} className='overflow-y-auto max-h-[70vh]'>
        <div
          className='grid'
          style={{ gridTemplateColumns: '48px repeat(7, 1fr)' }}
        >
          <div style={{ height: HOUR_HEIGHT * 24 }} className='relative'>
            {Array.from({ length: 24 }, (_, i) => (
              <div
                key={i}
                className='absolute left-0 right-0 text-[10px] text-neutral-500 text-right pr-1'
                style={{ top: i * HOUR_HEIGHT - 6 }}
              >
                {i === 0 ? '' : `${i % 12 === 0 ? 12 : i % 12}${i < 12 ? 'a' : 'p'}`}
              </div>
            ))}
          </div>

          {days.map((d) => (
            <TimeGridColumn
              key={toDateKey(d)}
              date={d}
              entries={byDay.get(toDateKey(d)) ?? []}
              canEdit={canEdit}
              onCreate={onCreate}
              onEditAvailability={onEditAvailability}
              onOpenBooking={onOpenBooking}
              now={now}
              namespace={namespace}
              showBookingDetails={showBookingDetails}
            />
          ))}
        </div>
      </div>
    </div>
  );
};
